// The one place that reads the delivery-attempt cap (GET
// /v1/parameters/max_delivery_attempts). The cap lives in Constantes so
// Operaciones can retune the state machine without a deploy (DMS-35); the
// backend is what enforces it, and the screens read it only to say how many
// attempts are left and to warn that the next one is the last.
//
// The call goes through the app's own origin; the BFF proxies it to the private
// API (never called from the browser directly).

import { type HttpApiClient } from "@vana/core";

/** The business-parameter key the backend reads the cap from. */
export const MAX_ATTEMPTS_PARAMETER_KEY = "max_delivery_attempts";

/** What the screens assume until the parameter answers. It matches the seeded
 *  value, so a slow or failed read shows the usual cap instead of an empty one;
 *  the backend still decides, and a 409 is what a stale default looks like. */
export const DEFAULT_MAX_ATTEMPTS = 2;

// Already camelCased by the response interceptor. `value` carries the numeric
// kinds, which is what `number` parameters like this one use.
interface ParameterResponse {
  data?: { key: string; value: number | null; kind: string };
}

/** Reads the delivery-attempt cap. Throws the raw transport error — each
 *  feature wraps it in its own domain error. */
export const fetchMaxAttempts = async (apiClient: HttpApiClient): Promise<number> => {
  const response = await apiClient.get<ParameterResponse>(
    `v1/parameters/${MAX_ATTEMPTS_PARAMETER_KEY}`,
  );
  const value = response.data?.value;
  // A missing, zero or fractional cap is unusable: keep the default rather than
  // let the screen offer an attempt the backend will refuse.
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : DEFAULT_MAX_ATTEMPTS;
};
